import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
    }

    private handleRetry = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="fixed inset-0 flex items-center justify-center bg-slate-50 p-6 z-[9999]">
                    <div className="text-center max-w-md w-full bg-white p-8 rounded-[2.5rem] shadow-xl border border-slate-100">
                        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8" />
                        </div>
                        <h1 className="text-xl font-black text-slate-900 mb-2">Something went wrong</h1>
                        <p className="text-slate-500 text-sm font-medium mb-6">
                            We encountered an unexpected error. Please try reloading the application.
                        </p>

                        {/* Dev Info */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <div className="bg-slate-900 text-slate-200 p-4 rounded-xl text-xs text-left overflow-auto max-h-40 mb-6 font-mono">
                                {this.state.error.toString()}
                            </div>
                        )}

                        <button
                            onClick={this.handleRetry}
                            className="bg-slate-900 text-white w-full py-4 rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Reload App
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
